import React, { useRef, useState } from 'react'
import { Button, Form, message, Input, Divider } from 'antd'
import classNames from 'classnames'
import { observer, useLocalObservable } from 'mobx-react-lite'
import { useTranslation } from '@/libs/next-lang'
import useFbe from '@/hooks/useFbe'
import Captcha from '@/components/Captcha'
import BaseLayout from '@/components/LoginModal/components/BaseLayout'
import { validateAccount } from '@/components/LoginModal/services'
import globalStore from '@/globalStore'
import CountDown from '@/page-components/components/CountDown'
import { sendVerifyCode } from '@/services/common'
import { goSSO, ssoButtonVisible } from '@/utils'
import {
  loginMobileReg,
  verifyCodeReg,
  loginPwdReg,
  loginPwdErrorMsg,
  emailReg,
} from '@/utils/validate'
import styles from './index.module.less'

const Forgot = () => {
  const [confirmLoading, setConfirmLoading] = useState<boolean>(false)
  const [form] = Form.useForm()
  const captchaInsRef = useRef(null)
  const globalMobx = useLocalObservable(() => globalStore)

  const { isFbe } = useFbe()

  const { t } = useTranslation(['user'])

  /**
   * 发送验证码
   * @param startFn
   */
  const handleSendCode = async (startFn: () => void) => {
    try {
      const values = await form.validateFields(['account'])
      const account = values.account
      if (loginMobileReg.test(account) || emailReg.test(account)) {
        // 人机校验
        const { validate } = await captchaInsRef.current.verify()
        startFn()
        const { data = {} } = await sendVerifyCode({ account, validate })
        if (data && data.code === 0) {
          message.success(t('user:t1'))
          process.env.NODE_ENV !== 'production' &&
            data.result &&
            form.setFieldsValue({ code: data.result })
        }
      } else {
        form.setFields([
          {
            name: ['account'],
            errors: [t('user:t14')],
            touched: false,
          },
        ])
      }
    } catch (error) {
      console.error('发送验证码失败: ', error)
    }
  }

  // 获取resetCode
  const getResetCode = async () => {
    try {
      // 获取resetCode
      const { data = {} } = await validateAccount({
        code: form.getFieldValue('code'),
        account: form.getFieldValue('account'),
      })
      if (data?.code === 0 && data?.success) {
        form.setFieldsValue({ resetCode: data?.result?.resetCode })
      }
      return Promise.resolve({
        code: data?.code,
        message: data?.message || t('user:t37'),
        resetCode: data?.result?.resetCode || 0,
      })
    } catch (err) {
      console.error('获取resetCode失败: ', err)
      return Promise.resolve({
        code: -1,
        message: t('user:t37'),
        resetCode: -1,
      })
    }
  }

  /**
   * 重置密码提交
   */
  const handleOk = () => {
    form.validateFields().then(async values => {
      if (confirmLoading) return
      setConfirmLoading(true)
      const { code, resetCode } = await getResetCode()
      if (code !== 0) {
        setConfirmLoading(false)
        return
      }
      const { success, data = {} } = await globalMobx.resetPassword({
        password: values.password,
        resetCode,
      })
      if (success) {
        message.success(t('user:t38'))
      } else {
        if (data?.message) {
          message.error(data?.message)
        }
        setConfirmLoading(false)
      }
    })
  }

  return (
    <BaseLayout>
      <h3 className={styles['lrf-title']}>{t('user:t19')}</h3>
      <div className={styles.register}>
        <span
          className={classNames('link-button', styles['register-button'])}
          onClick={() => {
            globalMobx.currentAction = 'login'
          }}
        >
          {t('user:t39')}
        </span>
      </div>
      <div className={styles['lrf-main']}>
        <Form
          layout="vertical"
          requiredMark={false}
          name="verify_form"
          className={styles.main}
          form={form}
        >
          <Form.Item
            style={{ marginBottom: 30 }}
            label=""
            name="account"
            rules={[
              { required: true, message: t('user:t13'), whitespace: true },
              () => ({
                validator(_, value) {
                  const v = (value || '').trim()
                  if (v && !loginMobileReg.test(v) && !emailReg.test(v)) {
                    return Promise.reject(new Error(t('user:t14')))
                  }
                  return Promise.resolve()
                },
              }),
            ]}
          >
            <Input
              suffix=" "
              size="large"
              placeholder={t('user:t15')}
              className="cover-autofill-bg-color"
            />
          </Form.Item>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Form.Item
              className={styles.password}
              style={{
                flex: 1,
                display: 'inline-block',
                marginBottom: 30,
                marginRight: 16,
              }}
              label=""
              name="code"
              rules={[
                { required: true, message: t('user:t2'), whitespace: true },
                () => ({
                  validator(_, value) {
                    const v = (value || '').trim()
                    if (v && !verifyCodeReg.test(v)) {
                      return Promise.reject(new Error(t('user:t3')))
                    }
                    return Promise.resolve()
                  },
                }),
              ]}
            >
              <Input
                style={{ width: '100%' }}
                suffix=" "
                size="large"
                placeholder={t('user:t4')}
                className="cover-autofill-bg-color"
              />
            </Form.Item>
            <Form.Item
              style={{
                width: '94px',
                textAlign: 'right',
                display: 'inline-block',
                marginBottom: 30,
              }}
            >
              {' '}
              <div className="psd-label">
                <CountDown
                  seconds={60}
                  render={(onStart, disabled, current) => {
                    if (disabled) {
                      return (
                        <Button
                          className={classNames(styles['verify-code-disabled'], styles['get-code'])}
                          size="large"
                          disabled={disabled}
                        >{`${current}s ${t('user:t5')}`}</Button>
                      )
                    }
                    return (
                      <Button
                        onClick={() => handleSendCode(onStart)}
                        size="large"
                        className={styles['get-code']}
                      >
                        {t('user:t6')}
                      </Button>
                    )
                  }}
                />
              </div>
            </Form.Item>
          </div>
          <Captcha ref={captchaInsRef} />
          <Form.Item
            style={{ marginBottom: 40 }}
            label=""
            name="password"
            // dependencies={['password']}
            rules={[
              {
                required: true,
                message: t('user:t16'),
                whitespace: true,
              },
              () => ({
                validator(_, value) {
                  const v = (value || '').trim()
                  if (v && !loginPwdReg.test(v) && !emailReg.test(v)) {
                    return Promise.reject(new Error(t('components:regexp.t1')))
                  }
                  return Promise.resolve()
                },
              }),
            ]}
          >
            <Input.Password
              type="password"
              size="large"
              placeholder={t('user:t18')}
              className="cover-autofill-bg-color"
            />
          </Form.Item>
          <div className={styles['submit-form-item']}>
            <Button
              type="primary"
              className={styles['submit-button']}
              size="large"
              loading={confirmLoading}
              onClick={handleOk}
            >
              {t('user:t36')}
            </Button>
          </div>
          {ssoButtonVisible() && !isFbe && (
            <div className={styles['sso-wrapper']}>
              <Divider plain className={styles['divider-line']}>
                {t('user:t10')}
              </Divider>
              <Button
                className={styles['sso-button']}
                onClick={() => {
                  // goSSO(`${window.location.origin}${globalMobx.targetPathUrl}`)
                  goSSO(`${window.location.origin}/sso`)
                }}
              >
                {t('user:t11')}
              </Button>
            </div>
          )}

          <Form.Item name="resetCode" style={{ height: 0, display: 'none' }} />
        </Form>
      </div>
    </BaseLayout>
  )
}

export default observer(Forgot)
